import React, { useState } from "react";
import dayjs from "utils/dayjs";
import { useI18next } from "gatsby-plugin-react-i18next";
import { ISO_FORMAT, DATEPICKER_DATE_FORMAT } from "store/constants/date";
import { RangePickerHorizontal } from "./RangePickerHorizontal";
import { RangePickerVertical } from "./RangePickerVertical";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import type { Dayjs } from "dayjs";
import { type DateRateFomatted } from "apis/calendarRates";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);

type RangePickerProps = {
  focusInput: "startDate" | "endDate";
  startDate: Dayjs;
  endDate: Dayjs;
  onChange: ({
    startDate,
    endDate,
  }: {
    startDate: Dayjs | string;
    endDate: Dayjs | string;
  }) => void;
  orientation: "vertical" | "horizontal";
  setStartDateValue: (date: string) => void;
  setEndDateValue: (date: string) => void;
  format?: (lang: string) => string;
  dateRate: Record<string, DateRateFomatted> | null;
  note?: string;
};

export const RangePicker = ({
  focusInput,
  startDate,
  endDate,
  onChange,
  orientation,
  setStartDateValue,
  setEndDateValue,
  format,
  dateRate,
  note,
}: RangePickerProps) => {
  // @ts-ignore
  const { language }: { language: "en" | "ja" | "zh" } = useI18next();
  dayjs.locale(language === "zh" ? "zh-cn" : language);
  const finalFormat = format?.(language) || DATEPICKER_DATE_FORMAT[language];
  const start = startDate || dayjs();
  const [state, setState] = useState<{
    start: Dayjs;
    end: Dayjs | null;
    startDateSelected: boolean;
    endDateSelected: boolean;
    hover: Dayjs | null;
  }>({
    start: start,
    end: endDate || start.add(1, "day"),

    // internal state for UI
    startDateSelected: false,
    endDateSelected: false,
    hover: null,
  });

  const onHoverDate = (date: Dayjs) => setState({ ...state, hover: date });

  const onClickDate = (date: Dayjs) => {
    if (state.startDateSelected) {
      if (date.isAfter(state.start)) {
        setState({ ...state, end: date, endDateSelected: true });
        onChange({
          startDate: state.start.format(ISO_FORMAT),
          endDate: date.format(ISO_FORMAT),
        });
        setStartDateValue(state.start.format(finalFormat));
        setEndDateValue(date.format(finalFormat));
      } else if (date.isBefore(state.start)) {
        setState({
          ...state,
          start: date,
          end: null,
          startDateSelected: true,
          endDateSelected: false,
        });
        setStartDateValue(date.format(finalFormat));
        setEndDateValue("");
      }
    } else {
      setState({
        ...state,
        start: date,
        end: null,
        startDateSelected: true,
        endDateSelected: false,
      });
      setStartDateValue(date.format(finalFormat));
      setEndDateValue("");
    }

    // In drawer mobile view
    // When start date and end date is already selected
    // but guest want to change dates in the calendar itself before clicking save button
    if (state.startDateSelected && state.endDateSelected) {
      setState({
        ...state,
        start: date,
        end: null,
        startDateSelected: true,
        endDateSelected: false,
      });
      setStartDateValue(date.format(finalFormat));
      setEndDateValue("");
    }
  };

  // This is the most complex one. If start date is selected, then
  // highlight other dates. If start date is not selected, then
  // don't highlight other dates
  const datePropsFn = (date: Dayjs) => {
    const start = state.start;
    const end = state.end || state.start;
    const hover = state.hover || state.start;

    const inBetweenSelected = dayjs(date).isBetween(start, end);

    if (!state.startDateSelected) {
      return {
        selected: date.isBetween(start, end, "day", "[]") || date.isSame(hover),
        inBetweenSelected,
      };
    } else {
      return {
        selected:
          date.isBetween(start, end, "day", "[]") ||
          (hover > start && date.isBetween(start, hover, "day", "[]")),
        inBetweenSelected,
      };
    }
  };

  return orientation === "vertical" ? (
    <RangePickerVertical
      firstMonth={dayjs().startOf("month")}
      datePropsFn={datePropsFn}
      onHoverDate={onHoverDate}
      onClickDate={onClickDate}
      dateRate={dateRate}
    />
  ) : (
    <RangePickerHorizontal
      firstMonth={start.startOf("month")}
      datePropsFn={datePropsFn}
      onHoverDate={onHoverDate}
      onClickDate={onClickDate}
      dateRate={dateRate}
      note={note}
    />
  );
};
